import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersJohannesburgPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isScrolled, setIsScrolled] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'Johannesburg Professional Painters | Expert Painting Services | paintersjohannesburg.com',
    description: 'Professional painting services in Johannesburg with 22+ years experience. Residential, commercial, industrial painting. Free quotes, quality workmanship, competitive rates. Serving all Johannesburg areas.',
    keywords: 'painters Johannesburg, painting services Johannesburg, house painters Johannesburg, commercial painters Johannesburg, residential painting Johannesburg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Johannesburg',
          description: 'Professional painting services in Johannesburg with over 22 years of experience',
          address: 'Johannesburg, Gauteng',
          priceRange: '$$',
          serviceArea: ['Sandton', 'Rosebank', 'Randburg', 'Fourways', 'Midrand']
        }),
        generateServiceSchema({
          name: 'Complete Painting Solutions',
          description: 'Residential, commercial, and industrial painting services',
          provider: 'Painters Johannesburg',
          areaServed: ['Johannesburg', 'Sandton', 'Rosebank', 'Randburg'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Industrial Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-paint-brush-line text-2xl text-[#e9f638]"></i>
              <span className="text-xl font-bold">Cape Town Painters</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get a Quote</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/776725dd286cf6e059927f07415b4eae.jpg"
            alt="Painters Johannesburg - Professional Painting Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 w-full">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Painters Johannesburg</h1>
          <p className="text-xl md:text-2xl mb-6">Professional Painting Services Across Johannesburg</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://paintersjohannesburg.com" target="_blank" rel="noopener noreferrer" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Visit Website</a>
            <Link to="/get-a-quote" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
          </div>
        </div>
      </div>

      {/* Quick Info Bar */}
      <div className="bg-[#e9f638] py-6">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-black">22+</div>
              <div className="text-black font-medium">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">5000+</div>
              <div className="text-black font-medium">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">98%</div>
              <div className="text-black font-medium">Client Satisfaction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">24/7</div>
              <div className="text-black font-medium">Customer Support</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b sticky top-[72px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-4 py-12">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">About Painters Johannesburg</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters Johannesburg</strong> has been the trusted name in professional painting services across Johannesburg and surrounding areas for over 22 years. Our commitment to excellence, attention to detail, and customer satisfaction has made us one of the most respected painting companies in Gauteng. We specialize in residential, commercial, and industrial painting projects of all sizes, delivering exceptional results that transform spaces and exceed expectations.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Company History and Expertise</h3>
              <p className="text-lg leading-relaxed mb-6">
                Founded in 2002, <strong>Painters Johannesburg</strong> began as a small family-owned business with a vision to provide high-quality painting services to the Johannesburg community. Over the past two decades, we have grown into a full-service painting company with a team of highly skilled professionals, state-of-the-art equipment, and an impressive portfolio of completed projects. Our journey has been marked by continuous learning, adaptation to new technologies, and an unwavering commitment to quality craftsmanship.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Throughout our 22 years in business, we have completed over 5,000 painting projects across Johannesburg, from small residential touch-ups to large-scale commercial renovations. Our experience spans across various property types including luxury homes, apartment complexes, office buildings, retail spaces, industrial facilities, and heritage properties. This diverse experience has equipped us with the knowledge and skills to handle any painting challenge with confidence and professionalism.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Why Choose Painters Johannesburg</h3>
              <p className="text-lg leading-relaxed mb-6">
                What sets <strong>Painters Johannesburg</strong> apart from other painting contractors is our holistic approach to every project. We don't just apply paint to walls; we transform spaces, enhance property values, and create environments that reflect our clients' vision and style. Our team takes the time to understand your specific needs, preferences, and budget constraints before developing a customized painting solution that delivers optimal results.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Our painters are not just skilled tradespeople; they are artists who take pride in their work. Each team member undergoes rigorous training in the latest painting techniques, safety protocols, and customer service standards. We invest in ongoing professional development to ensure our team stays current with industry trends, new products, and innovative application methods. This commitment to excellence is reflected in the quality of our work and the satisfaction of our clients.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Comprehensive Painting Solutions</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters Johannesburg</strong>, we offer a complete range of painting services designed to meet the diverse needs of our clients. Our residential painting services include interior and exterior painting, wallpaper installation and removal, decorative finishes, cabinet refinishing, deck and fence staining, and color consultation. We work with homeowners to create beautiful, personalized spaces that reflect their unique style and enhance their quality of life.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Our commercial painting services are tailored to minimize disruption to your business operations while delivering professional results that enhance your brand image and create a positive impression on customers and employees. We understand the importance of timelines in commercial projects and work efficiently to complete projects on schedule and within budget. Our commercial services include office painting, retail space painting, restaurant and hospitality painting, industrial facility painting, and building exterior painting.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Quality Materials and Sustainable Practices</h3>
              <p className="text-lg leading-relaxed mb-6">
                We believe that exceptional results start with quality materials. <strong>Painters Johannesburg</strong> partners with leading paint manufacturers to source premium products that deliver superior coverage, durability, and finish. We offer a wide selection of paint brands, colors, and finishes to suit every project requirement and budget. Our team can guide you through the selection process, helping you choose the right products for your specific application and environmental conditions.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Environmental responsibility is a core value at <strong>Painters Johannesburg</strong>. We are committed to sustainable painting practices that minimize environmental impact while delivering exceptional results. We offer eco-friendly paint options with low or zero VOC content, implement proper waste disposal procedures, use efficient application techniques to reduce material waste, and follow green building standards when applicable. Our commitment to sustainability extends to every aspect of our operations, from material selection to project cleanup.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Painting Process</h3>
              <p className="text-lg leading-relaxed mb-6">
                Every project at <strong>Painters Johannesburg</strong> follows a systematic process designed to ensure quality results and customer satisfaction. We begin with a comprehensive consultation where we assess your property, discuss your vision and requirements, provide expert recommendations, and develop a detailed project plan. Our transparent quoting process ensures you understand exactly what services are included and what to expect throughout the project.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Preparation is the foundation of quality painting work. Our team takes the time to properly prepare all surfaces before applying paint, including cleaning, sanding, filling cracks and holes, priming, and protecting surrounding areas. This meticulous preparation ensures optimal paint adhesion, smooth finishes, and long-lasting results. We never cut corners on preparation, as we know it's essential to the success of every project.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                During the painting phase, our skilled craftsmen apply paint using proven techniques that ensure even coverage, clean lines, and professional finishes. We maintain a clean, organized work environment and take care to protect your property throughout the project. Our attention to detail extends to every aspect of the work, from cutting in around trim and fixtures to achieving perfectly smooth roller marks and ensuring consistent color throughout.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Service Areas Across Johannesburg</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters Johannesburg</strong> proudly serves all areas of Johannesburg and surrounding suburbs. Our service area includes Sandton, Rosebank, Randburg, Fourways, Midrand, Roodepoort, Soweto, Alexandra, Bryanston, Morningside, Parktown, Houghton, Melrose, Illovo, Hyde Park, Craighall, Parkhurst, Greenside, Northcliff, Linden, Emmarentia, Auckland Park, Westdene, Melville, Norwood, Orange Grove, Cyrildene, Bedfordview, Edenvale, Kempton Park, Boksburg, Benoni, Germiston, Alberton, and many more areas throughout Greater Johannesburg.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Customer Satisfaction Guarantee</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters Johannesburg</strong>, your satisfaction is our top priority. We stand behind the quality of our work with comprehensive warranties and guarantees. If you're not completely satisfied with any aspect of our service, we'll work with you to make it right. Our 98% customer satisfaction rating is a testament to our commitment to excellence and our dedication to exceeding client expectations on every project.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We maintain open communication throughout every project, keeping you informed of progress, addressing any concerns promptly, and ensuring the final result meets or exceeds your expectations. Our project managers are always available to answer questions, provide updates, and ensure your complete satisfaction with our work.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Get Started Today</h3>
              <p className="text-lg leading-relaxed mb-6">
                Ready to transform your property with professional painting services? Contact <strong>Painters Johannesburg</strong> today for a free, no-obligation quote. Our friendly team is ready to discuss your project, answer your questions, and provide expert guidance on all your painting needs. With over 22 years of experience, thousands of satisfied customers, and a commitment to excellence, we're the painting partner you can trust to deliver exceptional results every time.
              </p>

              <div className="bg-[#e9f638] p-8 rounded-lg mt-8">
                <h4 className="text-2xl font-bold mb-4">Contact Information</h4>
                <div className="space-y-3">
                  <p className="flex items-center text-lg">
                    <i className="ri-global-line text-2xl mr-3"></i>
                    <a href="https://paintersjohannesburg.com" target="_blank" rel="noopener noreferrer" className="hover:underline font-medium">paintersjohannesburg.com</a>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-map-pin-line text-2xl mr-3"></i>
                    <span className="font-medium">Serving all Johannesburg areas</span>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-time-line text-2xl mr-3"></i>
                    <span className="font-medium">Monday - Saturday: 7:00 AM - 6:00 PM</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Painting Services</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20residential%20house%20interior%20painting%20living%20room%20walls%20fresh%20paint%20roller%20brushes%20professional%20setup%20clean%20simple%20background&width=400&height=300&seq=service-residential-jhb&orientation=landscape"
                    alt="Residential Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Residential Painting</h3>
                  <p className="text-gray-700 mb-4">Complete interior and exterior painting services for homes, including color consultation, surface preparation, and premium finishes.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Interior wall painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Exterior house painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Ceiling painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Trim and molding</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20commercial%20office%20space%20painting%20professional%20painters%20working%20on%20walls%20business%20environment%20clean%20organized%20simple%20background&width=400&height=300&seq=service-commercial-jhb&orientation=landscape"
                    alt="Commercial Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Commercial Painting</h3>
                  <p className="text-gray-700 mb-4">Professional painting solutions for businesses, offices, retail spaces, and commercial properties with minimal disruption.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Office painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Retail space painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Restaurant painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building exteriors</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=industrial%20facility%20warehouse%20painting%20large%20scale%20commercial%20painting%20equipment%20professional%20painters%20safety%20gear%20simple%20background&width=400&height=300&seq=service-industrial-jhb&orientation=landscape"
                    alt="Industrial Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Industrial Painting</h3>
                  <p className="text-gray-700 mb-4">Specialized coating and painting services for industrial facilities, warehouses, and manufacturing plants.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Warehouse painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Factory floor coating</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Equipment painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Safety line marking</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=decorative%20wall%20painting%20texture%20finish%20special%20effects%20artistic%20painting%20techniques%20elegant%20interior%20simple%20background&width=400&height=300&seq=service-decorative-jhb&orientation=landscape"
                    alt="Decorative Finishes"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Decorative Finishes</h3>
                  <p className="text-gray-700 mb-4">Custom decorative painting techniques including faux finishes, textures, and specialty coatings for unique aesthetics.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Faux finishes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Textured walls</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Venetian plaster</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Metallic finishes</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=exterior%20building%20painting%20scaffolding%20professional%20painters%20working%20on%20facade%20weather%20protection%20coating%20simple%20background&width=400&height=300&seq=service-exterior-jhb&orientation=landscape"
                    alt="Exterior Painting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Exterior Painting</h3>
                  <p className="text-gray-700 mb-4">Weather-resistant exterior painting services to protect and beautify your property's exterior surfaces.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building facades</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Roof painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Deck and fence staining</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Waterproofing</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=color%20consultation%20paint%20samples%20color%20swatches%20interior%20design%20planning%20professional%20advice%20simple%20background&width=400&height=300&seq=service-consultation-jhb&orientation=landscape"
                    alt="Color Consultation"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Color Consultation</h3>
                  <p className="text-gray-700 mb-4">Expert color consultation services to help you choose the perfect colors and finishes for your space.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Color scheme design</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Sample testing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Finish recommendations</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Trend guidance</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Work Gallery</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautifully%20painted%20modern%20living%20room%20interior%20fresh%20white%20walls%20elegant%20furniture%20natural%20lighting%20professional%20finish%20simple%20background&width=400&height=500&seq=gallery-jhb-1&orientation=portrait"
                  alt="Residential Interior Painting Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20commercial%20office%20space%20freshly%20painted%20walls%20professional%20finish%20clean%20lines%20business%20environment%20simple%20background&width=400&height=500&seq=gallery-jhb-2&orientation=portrait"
                  alt="Commercial Office Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautiful%20house%20exterior%20freshly%20painted%20facade%20modern%20architecture%20professional%20painting%20finish%20sunny%20day%20simple%20background&width=400&height=500&seq=gallery-jhb-3&orientation=portrait"
                  alt="Exterior House Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=elegant%20bedroom%20interior%20painting%20soft%20colors%20professional%20finish%20comfortable%20atmosphere%20modern%20design%20simple%20background&width=400&height=500&seq=gallery-jhb-4&orientation=portrait"
                  alt="Bedroom Painting Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20retail%20store%20interior%20freshly%20painted%20walls%20professional%20commercial%20finish%20bright%20lighting%20shopping%20environment%20simple%20background&width=400&height=500&seq=gallery-jhb-5&orientation=portrait"
                  alt="Retail Space Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=industrial%20warehouse%20interior%20painted%20walls%20professional%20coating%20clean%20finish%20large%20space%20simple%20background&width=400&height=500&seq=gallery-jhb-6&orientation=portrait"
                  alt="Industrial Facility Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Client Reviews</h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Painters Johannesburg did an outstanding job on our home in Sandton. The team was professional, punctual, and meticulous in their work. They transformed our living spaces with beautiful colors and flawless finishes. Highly recommend their services!"</p>
                <p className="font-semibold">- Sarah M., Sandton</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"We hired Painters Johannesburg for our office renovation in Rosebank. They completed the project ahead of schedule with minimal disruption to our business operations. The quality of work exceeded our expectations. Excellent service from start to finish!"</p>
                <p className="font-semibold">- David K., Rosebank</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"The exterior painting of our property in Fourways was completed to perfection. The team handled all the preparation work thoroughly and the final result looks amazing. Great value for money and exceptional customer service throughout the project."</p>
                <p className="font-semibold">- Michael T., Fourways</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"I'm extremely pleased with the interior painting work done by Painters Johannesburg. Their color consultation service helped me choose the perfect palette for my home. The painters were respectful, clean, and delivered beautiful results. Will definitely use them again!"</p>
                <p className="font-semibold">- Jennifer L., Randburg</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Outstanding service from Painters Johannesburg! They painted our retail space in Midrand and the transformation is incredible. The team worked efficiently, maintained a clean work area, and the quality of their work is top-notch. Highly professional company!"</p>
                <p className="font-semibold">- Robert P., Midrand</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-black text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Space?</h2>
          <p className="text-xl mb-8">Get a free quote from Painters Johannesburg today</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
            <a href="https://paintersjohannesburg.com" target="_blank" rel="noopener noreferrer" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Visit Website</a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#e9f638] text-black py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Cape Town Painters</h3>
              <p className="text-sm">Your trusted directory for professional painting services across South Africa.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:underline">Home</Link></li>
                <li><Link to="/about" className="hover:underline">About</Link></li>
                <li><Link to="/contact" className="hover:underline">Contact</Link></li>
                <li><Link to="/get-a-quote" className="hover:underline">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li>Residential Painting</li>
                <li>Commercial Painting</li>
                <li>Industrial Painting</li>
                <li>Decorative Finishes</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Cape Town, South Africa
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  info@painters-cape-town.co.za
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-black/20 pt-8 text-center text-sm">
            <p>&copy; 2024 Cape Town Painters Directory. All rights reserved.</p>
            <div className="mt-2 space-x-4">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">Powered by leaderr</a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">SEO by SEO Studio</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
